import React, { useCallback } from 'react';
import { Row, Col, Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { FilterSchema } from '../../types/customReport';
import MultiSelectFilter from './MultiSelectFilter';

interface Props {
  filters: FilterSchema[];
  values: Record<string, any>;
  uiValues: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onUiChange: (key: string, value: any) => void;
  onClear: () => void;
}

interface FilterItemProps {
  filter: FilterSchema;
  value: any;
  uiValue: any;
  isTodayActive: boolean;
  onChange: (key: string, value: any) => void;
  onUiChange: (key: string, value: any) => void;
}

const FilterItem: React.FC<FilterItemProps> = ({ 
  filter, 
  value, 
  uiValue, 
  isTodayActive, 
  onChange, 
  onUiChange 
}) => {
  const { t } = useTranslation(['pages', 'common']);

  const handleValueChange = useCallback((val: any) => {
    onChange(filter.key, val);
  }, [filter.key, onChange]);

  const handleUiChange = useCallback((isChecked: boolean) => {
    onUiChange(`${filter.key}_include_inactive`, isChecked);
    if (!isChecked) {
      onChange(filter.key, filter.type === 'multi_select' ? [] : '');
    }
  }, [filter.key, filter.type, onChange, onUiChange]);

  const handleInactiveDetected = useCallback(() => {
    onUiChange(`${filter.key}_include_inactive`, true);
  }, [filter.key, onUiChange]);

  const isDateFilter = ['date_from', 'date_to', 'from_date', 'to_date'].includes(filter.key);
  const disabled = isTodayActive && isDateFilter;

  const renderInput = () => {
    switch (filter.type) {
      case 'boolean':
        return (
          <div className="d-flex align-items-center h-100">
            <Form.Check
              type="switch"
              id={`filter-${filter.key}`}
              checked={!!value}
              onChange={(e) => handleValueChange(e.target.checked)}
              label={value ? t('common:status.active') : t('common:status.inactive')}
            />
          </div>
        );
      case 'date':
        return (
          <OverlayTrigger
            placement="top"
            overlay={disabled ? <Tooltip>{t('customReports.todayFilterActive')}</Tooltip> : <></>}
          >
            <div>
              <Form.Control
                type="date"
                size="sm"
                value={value || ''}
                onChange={(e) => handleValueChange(e.target.value)}
                required={filter.required && !disabled}
                disabled={disabled}
                style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
            </div>
          </OverlayTrigger>
        );
      case 'multi_select':
        return (
          <MultiSelectFilter
            filter={filter}
            value={value || []}
            onChange={handleValueChange}
            includeInactive={uiValue}
            onInactiveDetected={handleInactiveDetected}
          />
        );
      case 'select':
        return (
          <Form.Select
            size="sm"
            value={value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            required={filter.required}
          >
            <option value="">{t('customReports.selectPlaceholder')}</option>
            {filter.options?.map((opt) => {
              return <option key={opt} value={opt}>{opt}</option>;
            })}
          </Form.Select>
        );
      case 'text':
      default:
        return (
          <Form.Control
            type="text"
            size="sm"
            value={value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            required={filter.required}
          />
        );
    }
  };

  const isChildFilter = filter.source?.includes('children');

  return (
    <Form.Group>
      <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
        {filter.label}
        {filter.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      {renderInput()}
      {isChildFilter && (
        <Form.Check 
          type="switch"
          id={`include-inactive-${filter.key}`}
          label={t('common:labels.includeInactive')}
          className="mt-1 small text-muted"
          checked={!!uiValue}
          onChange={(e) => handleUiChange(e.target.checked)}
        />
      )}
    </Form.Group>
  );
};

export const FilterPanel: React.FC<Props> = ({ filters, values, uiValues, onChange, onUiChange, onClear }) => {
  const { t } = useTranslation(['pages', 'common']);

  if (filters.length === 0) {
    return <div className="text-muted text-center py-3">{t('customReports.noFiltersAvailable')}</div>;
  }

  const isTodayActive = values['is_today'] === true;

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <Button variant="outline-secondary" size="sm" onClick={onClear}>
          {t('customReports.clearFilters')}
        </Button>
      </div>
      <Row>
        {filters.map((filter) => (
          <Col md={6} key={filter.key} className="mb-3">
            <FilterItem
              filter={filter}
              value={values[filter.key]}
              uiValue={uiValues[`${filter.key}_include_inactive`]}
              isTodayActive={isTodayActive}
              onChange={onChange}
              onUiChange={onUiChange}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default FilterPanel;

